<script setup lang="ts">
import { useNuxtApp } from "#app";
import { ResizeType } from "@bitpatty/imgproxy-url-builder";

const props = defineProps({
    title: String,
    subTitle: String,
    category: Object,
    totalRating: Number,
    ratings: Array,
    price: Number,
    oldPrice: Number,
    images: Array,
    buyUrl: String,
    source: String,
    skeleton: Boolean,
    notLazyLoad: Boolean,
    useH1: Boolean,
    inReviewPage: Boolean,
    highlight: Boolean,
});
const nuxtApp = useNuxtApp();
</script>

<template>
    <section v-if="skeleton === false">
        <div
            :class="{
                'pb-10 lg:pb-20': !inReviewPage,
                'w-full p-6 sm:p-10 lg:p-20': inReviewPage,
                'bg-yellow-50': highlight,
                'bg-gray-100': !highlight,
            }"
            class="rounded-lg text-gray-800 relative md:text-left"
        >
            <div :class="{'container': !inReviewPage}" class="lg:flex items-center mx-auto">
                <div class="relative p-5 mb-10 sm:p-10 lg:mr-10 sm:mt-10 border-4 border-indigo-800">
                    <PartTotalRating :rating="totalRating" class="absolute -right-12 -top-12 z-10"/>
                    <Swiper
                        class="gallery sm:w-[20rem] sm:h-[20rem]"
                        :modules="[SwiperPagination, SwiperAutoplay]"
                        :pagination="{
                              clickable: true,
                            }"
                        :autoplay="{
                              delay: 8000,
                              disableOnInteraction: true,
                            }"
                        :loop="true"
                    >
                        <SwiperSlide v-for="(image, index) in images" :key="index">
                            <PartFancybox :href="nuxtApp.imgProxyResize(image, 1000)" :group-name="title">
                                <picture>
                                    <source media="(max-width: 766px)" type="image/webp" :srcset="`${nuxtApp.imgProxyResize(image, 320, 320, 'webp')} 1x, ${nuxtApp.imgProxyResize(image, 640, 640, 'webp')} 2x`">
                                    <source media="(max-width: 766px)" :srcset="`${nuxtApp.imgProxyResize(image, 320, 320)} 1x, ${nuxtApp.imgProxyResize(image, 640, 640)} 2x`">
                                    <source media="(min-width: 767px)" type="image/webp" :srcset="`${nuxtApp.imgProxyResize(image, 320, 320, 'webp')} 1x, ${nuxtApp.imgProxyResize(image, 640, 640, 'webp')} 2x`">
                                    <source media="(min-width: 767px)" :srcset="`${nuxtApp.imgProxyResize(image, 320, 320)} 1x, ${nuxtApp.imgProxyResize(image, 640, 640)} 2x`">
                                    <img
                                        :loading="notLazyLoad ? 'eager' : 'lazy'"
                                        width="115"
                                        height="115"
                                        class="w-full h-[320px] object-contain object-center rounded"
                                        :src="nuxtApp.imgProxyResize(image, 320, 320)"
                                        :alt="title"
                                    >
                                </picture>
                            </PartFancybox>
                        </SwiperSlide>
                    </Swiper>
                </div>
                <div class="w-full space-y-12">
                    <div class="prose min-w-full space-y-6">
                        <div>
                            <h1 v-if="useH1" class="text-4xl m-0">{{ title }}</h1>
                            <h2 v-else class="text-4xl m-0">{{ title }}</h2>
                            <div v-if="category">
                                <nuxt-link to="/shop" class="font-semibold text-indigo-800 mr-1 no-underline">
                                    Shop <span aria-hidden="true">&rarr;</span>
                                </nuxt-link>
                                <nuxt-link :to="category.url" class="font-semibold text-indigo-800 no-underline">
                                    {{ category.title }}
                                </nuxt-link>
                            </div>
                        </div>
                        <h3 v-if="subTitle" class="text-gray-600">{{ subTitle }}</h3>
                        <div v-if="ratings.length" class="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 justify-between gap-4">
                            <div v-for="rating in ratings" class="space-y-1">
                                <p class="m-0 text-gray-500">{{ rating.title }}</p>
                                <PartRating :maximum="5" :rating="rating.rating"></PartRating>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-wrap items-center space-x-3 sm:space-x-6">
                        <div :class="{'text-red-600': oldPrice}" class="relative inline-block align-bottom">
                            <PartPriceExtend
                                :price="price"
                                currencyClass="text-xl sm:text-2xl leading-none align-baseline"
                                pricePart1Class="font-bold text-2xl sm:text-5xl leading-none align-baseline"
                                pricePart2Class="sm:text-2xl leading-none align-baseline"
                            />
                            <div v-if="oldPrice" class="text-gray-500">
                                <PartPriceExtend
                                    :price="oldPrice"
                                    currencyClass="sm:text-xl leading-none align-baseline"
                                    pricePart1Class="font-bold sm:text-2xl leading-none align-baseline line-through decoration-red-600"
                                    pricePart2Class="sm:text-base leading-none align-baseline"
                                />
                            </div>
                        </div>
                        <div class="inline-block align-bottom">
                            <nuxt-link
                                :to="buyUrl"
                                target="_blank" class="bg-indigo-800 hover:bg-transparent transition ease-in duration-300 text-white hover:text-indigo-800 rounded-full px-10 py-2 font-semibold border-2 border-indigo-800"
                            >BUY NOW</nuxt-link>
                        </div>
                        <div v-if="source" class="inline-block align-bottom">
                            <span class="text-sm text-gray-400">on {{ source }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <section v-else>
        <div
            :class="{
                'pb-10 lg:pb-20': !inReviewPage,
                'w-full p-6 sm:p-10 lg:p-20': inReviewPage,
                'bg-yellow-50': highlight,
                'bg-gray-50': !highlight,
            }"
            class="rounded-lg text-gray-800 relative md:text-left"
        >
            <div :class="{'container': !inReviewPage}" class="lg:flex items-center mx-auto">
                <div class="relative p-5 mb-10 sm:p-10 sm:mr-10 sm:mt-10 border-4 border-indigo-800">
                    <Swiper
                        class="gallery sm:w-[20rem] sm:h-[20rem]"
                        :modules="[SwiperPagination, SwiperAutoplay]"
                        :pagination="{
                              clickable: true,
                            }"
                        :autoplay="{
                              delay: 8000,
                              disableOnInteraction: true,
                            }"
                        :loop="true"
                    >
                        <SwiperSlide v-for="index in 5" :key="index">
                            <picture class="animate-pulse h-20 sm:h-40 w-full object-center rounded bg-soft-white-100"></picture>
                        </SwiperSlide>
                    </Swiper>
                </div>
                <div class="w-full space-y-12">
                    <div class="prose min-w-full space-y-6">
                        <div>
                            <div class="animate-pulse rounded h-6 w-[70%] bg-gray-100"></div>
                        </div>
                        <div class="space-y-2">
                            <div class="animate-pulse rounded h-4 w-[40%] bg-gray-100"></div>
                            <div class="animate-pulse rounded h-4 w-[80%] bg-gray-100"></div>
                            <div class="animate-pulse rounded h-4 w-[60%] bg-gray-100"></div>
                        </div>
                        <div class="space-y-2">
                            <div class="animate-pulse rounded h-4 w-[40%] bg-gray-100"></div>
                            <div class="animate-pulse rounded h-4 w-[80%] bg-gray-100"></div>
                            <div class="animate-pulse rounded h-4 w-[60%] bg-gray-100"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>